/* custom code start */
if (!customElements.get('occ-cart-samples')) {
  class OccCartSamples extends HTMLElement {
    constructor() {
      super();

      this.toggle = this.querySelector('.occ-cart-samples__toggle');
      this.inputs = this.querySelectorAll('.occ-cart-samples__radio');
      this.addButton = this.querySelector('.occ-cart-samples__add-button');
      this.statusText = this.querySelector('.occ-cart-samples__status-text');
      this.feedback = this.querySelector('.occ-cart-samples__feedback');
      this.notice = this.querySelector('.occ-cart-samples-notice');

      this.minCartTotal = parseInt(this.dataset.minCartTotal || '0', 10);
      this.minCartItemCount = Math.max(1, parseInt(this.dataset.minCartItemCount || '1', 10));
      this.maxSampleSelections = Math.max(1, parseInt(this.dataset.maxSampleSelections || '1', 10));
      this.currentSampleCount = Math.max(0, parseInt(this.dataset.currentSampleCount || '0', 10));
      this.selectionPrompt = this.dataset.selectionPrompt || '[count] ücretsiz hediye seçin';
      this.sampleProductIds = (this.dataset.sampleProductIds || '')
        .split(',')
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id));
      this.sampleProductIdSet = new Set(this.sampleProductIds);
      this.sampleProductIdsInCart = new Set(
        Array.from(this.inputs)
          .filter((input) => input.disabled)
          .map((input) => parseInt(input.dataset.productId, 10))
          .filter((id) => !Number.isNaN(id))
      );

      this.toggle?.addEventListener('click', this.handleToggle.bind(this));
      this.inputs.forEach((input) => {
        input.addEventListener('change', this.handleSelection.bind(this));
      });
      this.addButton?.addEventListener('click', this.handleAddToCart.bind(this));
      this.notice?.addEventListener('click', this.handleNoticeClick.bind(this));

      this.applyInitialSelection();
    }

    connectedCallback() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.onCartUpdate(event);
        });
      }

      this.moveNoticeToTop();
      this.cleanupExcessSamples();
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      if (this.notice) this.notice.remove();
    }

    applyInitialSelection() {
      const firstAvailableInput = Array.from(this.inputs).find((input) => !input.disabled);
      const checkedInput = Array.from(this.inputs).find((input) => input.checked && !input.disabled);

      if (!checkedInput && firstAvailableInput) firstAvailableInput.checked = true;
      this.updateSelectionState();
    }

    moveNoticeToTop() {
      if (!this.notice || this.noticeMoved) return;

      const cartTitleWrapper = document.querySelector('cart-items .title-wrapper-with-link');
      const mainContent = document.getElementById('MainContent');

      if (cartTitleWrapper) {
        cartTitleWrapper.after(this.notice);
        this.noticeMoved = true;
      } else if (mainContent) {
        mainContent.prepend(this.notice);
        this.noticeMoved = true;
      }
    }

    handleNoticeClick() {
      if (this.classList.contains('is-collapsed')) {
        this.classList.remove('is-collapsed');
        this.toggle?.setAttribute('aria-expanded', 'true');
      }

      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    handleToggle() {
      const isCollapsed = this.classList.toggle('is-collapsed');
      this.toggle?.setAttribute('aria-expanded', String(!isCollapsed));
    }

    handleSelection(event) {
      const selectedInputs = this.getSelectedInputs();
      const availableSelections = this.getRemainingSelectionCount();

      if (event.target.checked && selectedInputs.length > availableSelections) {
        const inputsToUncheck = selectedInputs
          .filter((input) => input !== event.target)
          .slice(0, selectedInputs.length - availableSelections);

        inputsToUncheck.forEach((input) => {
          input.checked = false;
        });
      }

      this.clearStatus();
      this.updateSelectionState();
    }

    onCartUpdate(event) {
      if (event?.cartData) {
        this.updateVisibility(event.cartData);
        return;
      }

      this.fetchCart()
        .then((cartData) => this.updateVisibility(cartData))
        .catch(() => {});
    }

    fetchCart() {
      return fetch(window.Shopify.routes.root + 'cart.js', {
        headers: { Accept: 'application/json' },
      }).then((response) => {
        if (!response.ok) throw new Error(response.statusText);
        return response.json();
      });
    }

    getSampleItems(cartData) {
      return (cartData?.items || []).filter((item) => this.sampleProductIdSet.has(item.product_id));
    }

    getSampleQuantity(cartData) {
      return this.getSampleItems(cartData).reduce((total, item) => total + item.quantity, 0);
    }

    getRegularItemCount(cartData) {
      return (cartData?.items || [])
        .filter((item) => !this.sampleProductIdSet.has(item.product_id))
        .reduce((total, item) => total + item.quantity, 0);
    }

    getRemainingSelectionCount() {
      return Math.max(0, this.maxSampleSelections - this.currentSampleCount);
    }

    getSelectedInputs() {
      return Array.from(this.inputs).filter((input) => input.checked && !input.disabled);
    }

    updateVisibility(cartData) {
      if (!cartData || !Array.isArray(cartData.items)) return;

      const previousSampleProductIdsInCart = this.sampleProductIdsInCart;
      const sampleItems = this.getSampleItems(cartData);
      this.currentSampleCount = this.getSampleQuantity(cartData);
      this.sampleProductIdsInCart = new Set(sampleItems.map((item) => item.product_id));

      const meetsThreshold =
        cartData.total_price >= this.minCartTotal &&
        this.getRegularItemCount(cartData) >= this.minCartItemCount;
      const shouldShow = this.currentSampleCount < this.maxSampleSelections && meetsThreshold;

      this.classList.toggle('is-hidden', !shouldShow);
      if (this.notice) this.notice.hidden = !shouldShow;

      this.inputs.forEach((input) => {
        const productId = parseInt(input.dataset.productId, 10);
        const alreadyInCart = this.sampleProductIdsInCart.has(productId);

        // A disabled input can retain its checked state in the DOM. Clear it
        // when its sample has just been removed so it does not count as a
        // second selection once it becomes available again.
        if (alreadyInCart || previousSampleProductIdsInCart.has(productId)) {
          input.checked = false;
        }
        input.disabled = alreadyInCart;
      });

      if (shouldShow && this.getSelectedInputs().length === 0) {
        const firstAvailableInput = Array.from(this.inputs).find((input) => !input.disabled);
        if (firstAvailableInput) firstAvailableInput.checked = true;
      }

      this.updateSelectionState();
    }

    updateSelectionState() {
      const selectedInputs = this.getSelectedInputs();
      const remainingSelections = this.getRemainingSelectionCount();

      this.inputs.forEach((input) => {
        const productId = parseInt(input.dataset.productId, 10);
        const alreadyInCart = this.sampleProductIdsInCart.has(productId);
        input.disabled = alreadyInCart || remainingSelections === 0;
      });

      if (this.addButton) {
        this.addButton.disabled = selectedInputs.length === 0 || remainingSelections === 0;
      }

      if (this.feedback) {
        this.feedback.textContent = this.selectionPrompt.replace('[count]', this.maxSampleSelections);
      }
    }

    handleAddToCart() {
      if (this.addButton?.classList.contains('is-loading')) return;

      const selectedInputs = this.getSelectedInputs();
      if (!selectedInputs.length) {
        this.showStatus('Lütfen bir deneme ürünü seçin.', 'error');
        return;
      }

      this.addButton.classList.add('is-loading');
      this.addButton.disabled = true;
      this.clearStatus();

      this.fetchCart()
        .then((cartData) => {
          const currentSampleCount = this.getSampleQuantity(cartData);
          const availableSelections = Math.max(0, this.maxSampleSelections - currentSampleCount);
          const selectedProductsAlreadyInCart = selectedInputs.some((input) =>
            this.getSampleItems(cartData).some(
              (item) => item.product_id === parseInt(input.dataset.productId, 10)
            )
          );

          if (selectedProductsAlreadyInCart || selectedInputs.length > availableSelections) {
            this.updateVisibility(cartData);
            this.showStatus(
              `Sepetinizde en fazla ${this.maxSampleSelections} ücretsiz hediye olabilir.`,
              'error'
            );
            this.stopLoading();
            return;
          }

          this.addSamplesToCart(selectedInputs);
        })
        .catch(() => {
          this.showStatus('Sepet kontrol edilemedi. Lütfen tekrar deneyin.', 'error');
          this.stopLoading();
        });
    }

    addSamplesToCart(selectedInputs) {
      const body = JSON.stringify({
        items: selectedInputs.map((input) => ({
          id: parseInt(input.value, 10),
          quantity: 1,
        })),
      });

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.json();
        })
        .then(() => {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'occ-cart-samples' });
          }

          this.showStatus('Hediye ürünler sepete eklendi!', 'success');
          setTimeout(() => window.location.reload(), 600);
        })
        .catch(() => {
          this.showStatus('Bir hata oluştu. Lütfen tekrar deneyin.', 'error');
          this.stopLoading();
        });
    }

    cleanupExcessSamples() {
      this.fetchCart()
        .then((cartData) => {
          const sampleItems = this.getSampleItems(cartData);
          const totalQuantity = sampleItems.reduce((sum, item) => sum + item.quantity, 0);
          if (totalQuantity <= this.maxSampleSelections) return;

          let remainingQuantity = this.maxSampleSelections;
          const updates = {};
          sampleItems.forEach((item) => {
            const permittedQuantity = Math.min(item.quantity, remainingQuantity);
            updates[item.key] = permittedQuantity;
            remainingQuantity -= permittedQuantity;
          });

          return fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates }),
          }).then((response) => {
            if (response.ok) window.location.reload();
          });
        })
        .catch(() => {});
    }

    stopLoading() {
      this.addButton?.classList.remove('is-loading');
      this.updateSelectionState();
    }

    showStatus(message, type) {
      if (!this.statusText) return;
      this.statusText.textContent = message;
      this.statusText.className = 'occ-cart-samples__status-text';
      if (type) this.statusText.classList.add(`is-${type}`);
    }

    clearStatus() {
      if (!this.statusText) return;
      this.statusText.textContent = '';
      this.statusText.className = 'occ-cart-samples__status-text';
    }
  }

  customElements.define('occ-cart-samples', OccCartSamples);
}
/* custom code end */
